import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { media } from '../styles/media';

const propTypes = {
  children: PropTypes.any,
  className: PropTypes.string,
  col: PropTypes.number,
  colxs: PropTypes.number,
  colsm: PropTypes.number,
  colmd: PropTypes.number,
  collg: PropTypes.number,
  colxl: PropTypes.number,
  gridGap: PropTypes.number,
  mobileGap: PropTypes.number
};

/**
 * Returns a scss string calculation for the width
 *
 * @function getColumnWidth
 * @returns {string} the scss calculation for the width of the item
 * @param {number} columnSize the number of columns the item spans
 */
const getColumnWidth = columnSize => {
  const numberOfColumns = 12;
  let columnWidth = `calc(${(columnSize / numberOfColumns) * 100}%)`;

  return columnWidth;
};

/**
 * Returns a media query for a given breakpoint size and column size
 *
 * @function constructQuery
 * @returns {string} a media query including the new flex-basis
 * @param {string} breakpoint the breakpoint in pixels (including px)
 * @param {number} columnSize the number of columns the item spans
 */
const constructQuery = (breakpoint, columnSize) => {
  return `@media (max-width: ${breakpoint}) {
        width: ${getColumnWidth(columnSize)};
  }`;
};

/**
 * Returns a set of media queries and styles for given column sizes
 *
 * @function getQueries
 * @returns {string} the relevant media queries and styles
 * @param {string} colxs the number of columns the item spans at extra small
 * @param {string} colsm the number of columns the item spans at small
 * @param {string} colmd the number of columns the item spans at medium
 * @param {string} collg the number of columns the item spans at large
 * @param {string} colxl the number of columns the item spans at extra large
 */
const getQueries = (colxs, colsm, colmd, collg, colxl) => {
  let queries = '';

  if (colxl) {
    queries += constructQuery(media.largeTop, colxl);
  }

  if (collg) {
    queries += constructQuery(media.mediumTop, collg);
  }

  if (colmd) {
    queries += constructQuery(media.mediumBottom, colmd);
  }

  if (colsm) {
    queries += constructQuery(media.smallTop, colsm);
  }

  if (colxs) {
    queries += constructQuery(media.smallBottom, colxs);
  }

  return queries;
};

const GridItem = props => {
  const { children, className, col, colxs, colsm, colmd, collg, colxl, gridGap, mobileGap, style, height } = props;

  return (
    <StyledGridItem
      className={className}
      style={style}
      cols={{ col, colxs, colsm, colmd, collg, colxl }}
      gridGap={gridGap}
      mobileGap={mobileGap}
      height={height}
    >
      {children}
    </StyledGridItem>
  );
};

const StyledGridItem = styled.div`
  display: flex;
  position: relative;
  height: ${({ height }) => height ? `${height}px` : '' };
  padding: ${({ gridGap }) => `${gridGap}px 0 0 ${gridGap}px`};
  background-clip: padding-box;
  width: ${({ cols, gridGap }) => (cols.col ? getColumnWidth(cols.col, gridGap) : '100%')};

  ${({ cols }) => getQueries(cols.colxs, cols.colsm, cols.colmd, cols.collg, cols.colxl)}

  @media screen and (max-width: ${media.mediumTop}) {
    padding: ${({ mobileGap }) => `${mobileGap}px 0 0 ${mobileGap}px`};
  }
`;

GridItem.propTypes = propTypes;

export { GridItem };
